import { useEffect, useState, useCallback } from "react";
import { useRazorpay } from "react-razorpay";
import { toast } from "sonner";
import {
  ArrowRight, Heart, Shield, Loader2, CheckCircle2, Sparkles, Construction,
  Sun, Droplets, Stethoscope, UtensilsCrossed, Calendar, PawPrint,
  HomeIcon, Trees, AlertCircle, Receipt, MapPin, Moon, Eye, BadgeCheck, HelpCircle,
  ChefHat, Fence, Recycle, MessageCircle
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { createDonationOrder, verifyDonation, getStats, getRecentDonations } from "../lib/api";

const CAMPAIGN_GOAL = 5000000;
const PRESETS = [500, 1000, 2500, 5000, 10000];

// 10 frame sections with funding goals — Monthly Feeding leads
const FRAMES = [
  { icon: UtensilsCrossed, title: "Monthly Feeding", desc: "Twelve months of food, supplements, and special diets for disabled dogs.", goal: 1440000 },
  { icon: Stethoscope, title: "Recovery Ward", desc: "Surgical, post-op and trauma recovery rooms — climate-controlled, 24×7 vet on call.", goal: 800000 },
  { icon: AlertCircle, title: "Medical & Emergency Care", desc: "ICU, surgery table, blood-bank tie-ups for hit-and-run cases.", goal: 700000 },
  { icon: Trees, title: "Cage-Free Gazebo Zones", desc: "Open-air sleeping pods with raised beds — no bars, no concrete.", goal: 600000 },
  { icon: Sun, title: "Solar Panels & Power", desc: "Off-grid solar setup with a Distribution Panel for uninterrupted shelter power.", goal: 450000 },
  { icon: Droplets, title: "Water Borewell System", desc: "Dedicated borewell + filtration so 300+ dogs always have clean water.", goal: 350000 },
  { icon: Fence, title: "Fencing of the One Acre Shelter Land", desc: "Secure perimeter fencing across the full acre — safety for every rescued life inside.", goal: 500000 },
  { icon: ChefHat, title: "Kitchen & Food Storage Room Setup", desc: "Hygienic kitchen, refrigeration and bulk storage for daily meals across 300+ dogs.", goal: 400000 },
  { icon: Recycle, title: "Septic Tank & Waste Disposal System", desc: "Underground septic, drainage and waste-management for a clean, disease-free shelter.", goal: 300000 },
  { icon: PawPrint, title: "Puppy Isolation Garden", desc: "Quarantine zone for fragile pups & nursing mothers — fenced, sunlit, soft ground.", goal: 200000 },
];

const STATS = [
  { value: "300+", label: "Dogs in care" },
  { value: "50+", label: "Disabled / paralyzed dogs" },
  { value: "5", label: "Forced relocations" },
  { value: "1", label: "Acre forever home" },
  { value: "∞", label: "Meals served" },
];

const HERO_IMG = "/donate-hero.jpg";

export default function Donate() {
  const { Razorpay } = useRazorpay();
  const [amount, setAmount] = useState(1000);
  const [customMode, setCustomMode] = useState(false);
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "", phone: "", message: "" });
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [highlight, setHighlight] = useState(false);

  const refreshLive = useCallback(() => {
    getStats().then(setStats).catch(() => {});
    getRecentDonations(14).then(setRecent).catch(() => {});
  }, []);

  useEffect(() => {
    refreshLive();
  }, [refreshLive]);

  const finalAmount = customMode ? parseInt(custom || 0, 10) : amount;
  const raised = stats?.total_donations_inr ?? 300000;
  const pct = Math.min(Math.round((raised / CAMPAIGN_GOAL) * 100), 100);

  const updateDonor = (k, v) => setDonor((s) => ({ ...s, [k]: v }));

  const handleDonate = async () => {
    if (!finalAmount || finalAmount < 100) {
      toast.error("Please enter at least ₹100");
      return;
    }
    if (!donor.name || !donor.email) {
      toast.error("Please share your name and email");
      return;
    }
    setLoading(true);

    // Meta Pixel — InitiateCheckout (donor intent / funnel top)
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "InitiateCheckout", {
        value: finalAmount,
        currency: "INR",
        content_name: "Donation",
        content_category: "Donation",
      });
    }

    try {
      const order = await createDonationOrder({
        amount: finalAmount,
        name: donor.name,
        email: donor.email,
        phone: donor.phone || null,
        message: donor.message?.trim() || null,
        anonymous: anonymous,
      });

      if (order.mock) {
        await verifyDonation({
          donation_id: order.donation_id,
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `mock_pay_${Date.now()}`,
          razorpay_signature: "mock_signature",
        });
        // Meta Pixel — Purchase (mock flow, also a successful conversion locally)
        if (typeof window !== "undefined" && typeof window.fbq === "function") {
          window.fbq("track", "Purchase", {
            value: finalAmount,
            currency: "INR",
            content_name: "Donation",
            content_category: "Donation",
          });
        }
        setSuccess({ amount: finalAmount, mock: true });
        refreshLive();
        setLoading(false);
        return;
      }

      const options = {
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Pawsomeland Foundation",
        description: "Returning Back Their Space On Earth",
        order_id: order.order_id,
        prefill: { name: donor.name, email: donor.email, contact: donor.phone || "" },
        theme: { color: "#C85A40" },
        handler: async (response) => {
          try {
            await verifyDonation({
              donation_id: order.donation_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            // Meta Pixel — Purchase (verified live payment, THE conversion)
            if (typeof window !== "undefined" && typeof window.fbq === "function") {
              window.fbq("track", "Purchase", {
                value: finalAmount,
                currency: "INR",
                content_name: "Donation",
                content_category: "Donation",
              });
            }
            setSuccess({ amount: finalAmount, mock: false });
            refreshLive();
          } catch {
            toast.error("Payment verification failed. Please contact us.");
          } finally {
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };

      const RazorpayCtor = Razorpay || (typeof window !== "undefined" && window.Razorpay);
      if (!RazorpayCtor) {
        toast.error("Payment SDK is still loading. Please try again in a moment.");
        setLoading(false);
        return;
      }
      const rzp = new RazorpayCtor(options);
      rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setLoading(false); });
      rzp.open();
    } catch (err) {
      console.error("Donation error:", err);
      toast.error(err?.response?.data?.detail || err?.message || "Could not start donation.");
      setLoading(false);
    }
  };

  const scrollToDonate = () => {
    const card = document.getElementById("donate-sticky-card");
    if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlight(true);
    // Focus the name field so user can immediately start typing
    setTimeout(() => {
      const nameInput = document.querySelector('[data-testid="sticky-name-input"]');
      if (nameInput) nameInput.focus({ preventScroll: true });
    }, 450);
    setTimeout(() => setHighlight(false), 1800);
  };

  return (
    <div className="bg-bone" data-testid="donate-page">
      {/* ───────── CINEMATIC HERO ───────── */}
      <section className="relative h-[88vh] min-h-[640px] overflow-hidden" data-testid="campaign-hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Two rescued stray puppies hugging each other"
            className="w-full h-full object-cover"
            style={{ objectPosition: "65% 25%" }}
          />
          {/* Cinematic grain + warm gradients */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/35 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-16">
            <div className="max-w-3xl space-y-7 fade-up">
              <div className="inline-flex items-center gap-2 bg-honey/95 text-forest px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] font-bold">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest opacity-60"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-forest"></span>
                </span>
                Active Construction Campaign
              </div>
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl font-light text-bone leading-[0.92] tracking-tight">
                Returning back<br/>
                their <em className="italic text-honey">space on earth.</em>
              </h1>
              <p className="text-lg lg:text-2xl text-bone/85 max-w-2xl leading-relaxed font-light">
                After being forced to vacate five times, we decided it was finally time to build them a forever home.
              </p>
              <div className="flex flex-wrap items-center gap-4 pt-2">
                <Button
                  onClick={scrollToDonate}
                  className="bg-terracotta hover:bg-terracotta-deep text-bone rounded-full h-14 px-9 text-base font-semibold tracking-wide group shadow-[0_15px_40px_rgba(200,90,64,0.45)]"
                  data-testid="hero-help-build-cta"
                >
                  Help us build their forever home
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" strokeWidth={1.8}/>
                </Button>
                <div className="text-sm text-bone/70 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-honey" strokeWidth={1.5}/>
                  80G tax exempt · 100% secure
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── STATS STRIP ───────── */}
      <section className="bg-forest text-bone py-10 lg:py-14 border-y border-bone/10" data-testid="campaign-stats">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-4">
            {STATS.map((s, i) => (
              <div key={i} className="text-center md:text-left md:border-l md:first:border-l-0 border-bone/15 md:pl-6">
                <div className="font-serif text-5xl lg:text-6xl text-honey font-light leading-none">{s.value}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-bone/65 mt-3">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── DONATION CARD + EMOTIONAL STORY ───────── */}
      <section className="relative py-20 lg:py-32" data-testid="campaign-story-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">

          {/* DONATION CARD — appears first on mobile, right column on desktop */}
          <aside className="lg:col-span-5 xl:col-span-4 lg:order-last">
            <div id="donate-sticky-card" data-testid="sticky-donation-card">
              <div className={`bg-white rounded-[2rem] border shadow-[0_30px_60px_rgba(30,59,39,0.10)] overflow-hidden transition-all duration-500 ${highlight ? "border-terracotta ring-4 ring-terracotta/30 scale-[1.015] shadow-[0_40px_80px_rgba(200,90,64,0.35)]" : "border-bone-soft"}`}>
                {/* Progress header */}
                <div className="p-7 lg:p-8 border-b border-bone-soft">
                  <div className="flex items-baseline justify-between gap-2 mb-3">
                    <div className="font-serif text-3xl lg:text-4xl text-forest font-light">
                      ₹{raised < 100000 ? raised.toLocaleString("en-IN") : `${(raised / 100000).toFixed(2)}L`} <span className="text-sm text-ink/55">raised</span>
                    </div>
                    <span className="text-xs font-bold bg-terracotta/10 text-terracotta px-2.5 py-1 rounded-full">{pct}%</span>
                  </div>
                  <div className="h-2.5 bg-bone-soft rounded-full overflow-hidden relative mb-3">
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-terracotta to-honey rounded-full transition-all duration-1000" style={{ width: `${pct}%` }}/>
                  </div>
                  <div className="flex justify-between text-xs text-ink/60">
                    <span><span className="font-semibold text-forest">{(stats?.donor_count ?? 215).toLocaleString("en-IN")}</span> donors</span>
                    <span>Goal: ₹{(CAMPAIGN_GOAL / 100000).toFixed(0)}L</span>
                  </div>
                </div>

                {/* Amount selection */}
                <div className="p-7 lg:p-8 space-y-5">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-ink/60 mb-3 block">Choose amount</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {PRESETS.map((p) => {
                        const active = !customMode && amount === p;
                        return (
                          <button
                            key={p}
                            type="button"
                            onClick={() => { setAmount(p); setCustomMode(false); }}
                            className={`rounded-xl py-3 text-sm font-semibold border-2 transition-all ${
                              active
                                ? "border-terracotta bg-terracotta text-bone"
                                : "border-bone-soft hover:border-terracotta/50 text-forest"
                            }`}
                            data-testid={`sticky-amount-${p}`}
                          >
                            ₹{p >= 1000 ? `${p/1000}K` : p}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setCustomMode(true)}
                      className={`mt-2.5 text-xs font-medium ${customMode ? "text-terracotta" : "text-ink/60 hover:text-terracotta"}`}
                      data-testid="sticky-custom-toggle"
                    >
                      {customMode ? "← Pick preset amount" : "Enter custom amount →"}
                    </button>
                    {customMode && (
                      <Input
                        type="number"
                        min="100"
                        placeholder="e.g. 2500"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        className="mt-3 h-12 rounded-xl"
                        data-testid="sticky-custom-input"
                      />
                    )}
                  </div>

                  {/* Donor details */}
                  <div className="space-y-3 pt-2 border-t border-bone-soft">
                    <Input
                      placeholder="Your name *"
                      value={donor.name}
                      onChange={(e) => updateDonor("name", e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="sticky-name-input"
                    />
                    <Input
                      type="email"
                      placeholder="Email * (for 80G receipt)"
                      value={donor.email}
                      onChange={(e) => updateDonor("email", e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="sticky-email-input"
                    />
                    <Input
                      type="tel"
                      placeholder="Phone (optional)"
                      value={donor.phone}
                      onChange={(e) => updateDonor("phone", e.target.value)}
                      className="h-12 rounded-xl"
                      data-testid="sticky-phone-input"
                    />
                    <textarea
                      placeholder="Leave a message for the team (optional)"
                      value={donor.message}
                      onChange={(e) => updateDonor("message", e.target.value)}
                      rows={2}
                      maxLength={240}
                      className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                      data-testid="sticky-message-input"
                    />
                    <label className="flex items-center gap-2.5 text-sm text-ink/75 cursor-pointer pt-1" htmlFor="anon-check">
                      <Checkbox
                        id="anon-check"
                        checked={anonymous}
                        onCheckedChange={(v) => setAnonymous(!!v)}
                        data-testid="sticky-anonymous-checkbox"
                      />
                      <span>Donate anonymously <span className="text-ink/45">(your name won't be shown publicly)</span></span>
                    </label>
                  </div>

                  <Button
                    onClick={handleDonate}
                    disabled={loading}
                    className="w-full bg-terracotta hover:bg-terracotta-deep text-bone rounded-2xl h-14 text-base font-semibold tracking-wide shadow-[0_10px_30px_rgba(200,90,64,0.35)] mt-1"
                    data-testid="sticky-donate-button"
                  >
                    {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Processing…</>) : (
                      <>Donate ₹{(finalAmount || 0).toLocaleString("en-IN")} <ArrowRight className="w-4 h-4 ml-2"/></>
                    )}
                  </Button>

                  {/* Payment methods accepted */}
                  <div className="pt-1" data-testid="payment-methods-row">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-ink/45 text-center mb-2">We accept</div>
                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                      <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-white border border-bone-soft text-[11px] font-semibold" title="Google Pay">
                        <span style={{color:"#4285F4"}}>G</span><span style={{color:"#EA4335"}}>P</span><span style={{color:"#FBBC04"}}>a</span><span style={{color:"#34A853"}}>y</span>
                      </span>
                      <span className="inline-flex items-center h-7 px-2.5 rounded-md text-[11px] font-bold tracking-tight text-white" style={{background:"#5F259F"}} title="PhonePe">
                        Phone<span className="italic">Pe</span>
                      </span>
                      <span className="inline-flex items-center h-7 px-2.5 rounded-md text-[11px] font-bold tracking-tight text-white" style={{background:"#00BAF2"}} title="Paytm">
                        Paytm
                      </span>
                      <span className="inline-flex items-center h-7 px-2.5 rounded-md bg-forest text-bone text-[11px] font-bold tracking-tight" title="UPI">
                        UPI
                      </span>
                      <span className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-white border border-bone-soft text-[11px] font-semibold text-ink/70" title="Credit / Debit cards">
                        <span className="inline-block w-3.5 h-2.5 rounded-[2px]" style={{background:"linear-gradient(90deg,#1A1F71 50%,#F7B600 50%)"}}/>
                        Cards
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-ink/55">
                    <Shield className="w-3 h-3 text-terracotta" strokeWidth={2}/>
                    100% secure · Powered by Razorpay
                  </div>
                </div>

                {/* Live ticker / urgency */}
                <div className="bg-forest text-bone px-7 py-4 flex items-center gap-3">
                  <Construction className="w-5 h-5 text-honey shrink-0" strokeWidth={1.5}/>
                  <div className="text-sm leading-tight">
                    <div className="font-semibold">Foundation work begins this month</div>
                    <div className="text-bone/60 text-xs">Every rupee is a brick laid sooner.</div>
                  </div>
                </div>
              </div>

              {/* Trust note below card */}
              <div className="mt-5 text-xs text-ink/55 leading-relaxed px-2">
                Pawsomeland Foundation is a registered Section 8 non-profit. All donations are eligible for 80G tax exemption.
              </div>
            </div>
          </aside>

          {/* LEFT: Editorial story content + frames + closing block */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-20 lg:space-y-28">

            {/* Emotional write-up */}
            <div className="space-y-7" data-testid="emotional-writeup">
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-terracotta">
                <span className="w-10 h-px bg-terracotta" />
                Our promise
              </div>
              <h2 className="font-serif text-4xl lg:text-6xl font-light text-forest leading-[1.05] tracking-tight">
                We started with nothing — except a <em className="italic text-terracotta">promise.</em>
              </h2>
              <div className="space-y-5 text-lg lg:text-xl text-ink/80 leading-[1.8]">
                <p>
                  We started with nothing except a promise to never leave them behind.
                </p>

                {/* The promise — the dogs we feed and care for every single day */}
                <div className="!my-10 lg:!my-12 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/s1.jpg" alt="Rescued dogs eating from bowls at mealtime" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/shelter/3.jpg" alt="A dog beside the daily cooking setup at the shelter" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="col-span-2 sm:col-span-1 rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/n1.jpg" alt="A caretaker walks through the yard with a pack of rescued dogs" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                </div>

                <p>
                  After being forced to vacate again and again, carrying injured dogs from one temporary place to another, we realised survival was no longer enough.
                </p>

                {/* The new permanent home on dry land */}
                <div className="!my-10 lg:!my-12 rounded-xl overflow-hidden shadow-[0_25px_50px_rgba(30,59,39,0.18)]">
                  <img src="/shelter/1.jpg" alt="The new shelter structure under construction on dry land" loading="lazy" className="w-full aspect-[16/10] object-cover"/>
                </div>

                <p className="font-serif text-2xl lg:text-3xl text-forest italic leading-snug py-4">
                  They deserved permanence. Safety. Space to run. Space to heal. Space to grow old without fear.
                </p>

                {/* The dogs who deserve space — pack + portrait */}
                <div className="!my-10 lg:!my-12 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/shelter/5.jpg" alt="A rescuer surrounded by the rescued pack at the sanctuary" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/n5.jpg" alt="A rescued dog resting calmly outside the new shelter" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                </div>

                <p>
                  On this one acre of land, we have begun building a 3,000 sq. ft. recovery and rehabilitation centre for the 300+ rescued dogs in our care.
                </p>

                {/* The one acre — atmospheric land + structure (3-grid) */}
                <div className="!my-10 lg:!my-12 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="story-collage">
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/n3.jpg" alt="The one-acre land at sunset, framed by trees and fencing" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/sh3.jpg" alt="Wide view of the corrugated shelter structure on the property" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="col-span-2 sm:col-span-1 rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/n4.jpg" alt="The shelter at night — work continuing past sundown" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                </div>

                <p>
                  The first phase of construction has already been brought to life. Walls have come up, the structure has begun standing, and after years of uncertainty, the foundation of a permanent recovery space finally exists.
                </p>

                {/* First phase — the structure across times of day (3-grid) */}
                <div className="!my-10 lg:!my-12 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/shelter/2.jpg" alt="The shelter at sunset — translucent walls already in place" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/shelter/4.jpg" alt="A founder capturing the moment as the structure finally takes shape" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                  <div className="col-span-2 sm:col-span-1 rounded-xl overflow-hidden shadow-[0_20px_40px_rgba(30,59,39,0.15)]">
                    <img src="/story/n2.jpg" alt="Dogs gathered with their rescuer at the shelter at sunset" loading="lazy" className="w-full aspect-[4/5] object-cover"/>
                  </div>
                </div>

                <p>
                  But as construction progressed, our funds slowly ran out, forcing the work to pause midway. We had hoped to at least complete the flooring for the paralysed dogs who suffer daily on rough ground, but between rescue emergencies, medical care, feeding costs, and the realities of caring for 300+ lives, we could not carry it forward alone.
                </p>

                {/* The unfinished floor */}
                <div className="!my-10 lg:!my-12 rounded-xl overflow-hidden shadow-[0_25px_50px_rgba(30,59,39,0.18)]">
                  <img src="/story/s2.jpg" alt="The unfinished, rough floor that still wounds the paralysed dogs" loading="lazy" className="w-full aspect-[16/10] object-cover"/>
                </div>

                {/* Last frame — joy that's worth holding the reins for */}
                <div className="!my-10 lg:!my-12 rounded-xl overflow-hidden shadow-[0_30px_60px_rgba(30,59,39,0.22)] max-w-md mx-auto">
                  <img src="/story/s5.jpg" alt="A moment of pure joy with the rescued dogs at home" loading="lazy" className="w-full h-auto"/>
                </div>

                <p className="font-serif text-xl lg:text-2xl text-forest leading-snug pt-2">
                  Today, we are asking kind-hearted people to help us hold the reins with us — to help us continue building this recovery centre into a safe, functional, and lasting home for the hundreds of rescued lives who depend on it every single day.
                </p>
              </div>
            </div>

            {/* ───────── 10 FRAMES (inside sticky-parent left column) ───────── */}
            <div data-testid="frames-section">
              <div className="mb-10 max-w-2xl">
                <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-terracotta mb-5">
                  <span className="w-10 h-px bg-terracotta" />
                  What you're building
                </div>
                <h3 className="font-serif text-3xl lg:text-5xl font-light text-forest leading-tight mb-3">
                  Ten parts of <em className="italic text-terracotta">one home.</em>
                </h3>
                <p className="text-ink/70 text-base lg:text-lg leading-relaxed">
                  Every donation funds a specific piece. As construction progresses, each frame below will fill with real photos, blueprints and stories from the ground.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {FRAMES.map((f, i) => {
                  const goalDisplay = f.goal >= 100000
                    ? `₹${(f.goal / 100000).toFixed(f.goal % 100000 === 0 ? 0 : 1)}L`
                    : `₹${(f.goal / 1000).toFixed(0)}K`;
                  return (
                    <article
                      key={i}
                      className="group bg-white rounded-3xl p-6 lg:p-7 border border-bone-soft hover:border-terracotta/40 hover:-translate-y-1 transition-all duration-300 relative overflow-hidden"
                      data-testid={`frame-card-${i}`}
                    >
                      <div className="w-12 h-12 rounded-2xl bg-terracotta/10 flex items-center justify-center mb-4 group-hover:bg-terracotta group-hover:rotate-6 transition-all">
                        <f.icon className="w-5 h-5 text-terracotta group-hover:text-bone transition-colors" strokeWidth={1.6}/>
                      </div>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/40 block mb-2">{(i + 1).toString().padStart(2, "0")}</span>
                      <h4 className="font-serif text-xl text-forest leading-tight">{f.title}</h4>
                      <p className="text-sm text-ink/70 mt-2 leading-relaxed">{f.desc}</p>
                      <div className="mt-5 pt-5 border-t border-bone-soft flex items-end justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-ink/45 font-semibold">Goal</div>
                          <div className="font-serif text-3xl lg:text-4xl text-terracotta font-medium leading-none mt-1.5" data-testid={`frame-goal-${i}`}>{goalDisplay}</div>
                        </div>
                        <button
                          type="button"
                          onClick={scrollToDonate}
                          className="text-terracotta font-semibold text-sm link-underline shrink-0 pb-0.5"
                          data-testid={`frame-fund-${i}`}
                        >
                          Fund this →
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            {/* ───────── CLOSING EMOTIONAL BLOCK (inside sticky-parent left column) ───────── */}
            <div data-testid="closing-block-section">
              <div className="relative rounded-[2.5rem] overflow-hidden bg-forest text-bone p-10 lg:p-16">
                <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-honey/10 blur-3xl"/>
                <div className="absolute -left-10 -bottom-10 w-72 h-72 rounded-full bg-terracotta/15 blur-3xl"/>
                <div className="relative space-y-5 max-w-2xl">
                  <Sparkles className="w-8 h-8 text-honey" strokeWidth={1.3}/>
                  <h3 className="font-serif text-3xl lg:text-5xl font-light leading-tight">
                    Your name on every brick.
                  </h3>
                  <p className="text-bone/80 leading-relaxed text-base lg:text-lg">
                    Every donor will be listed on the foundation wall of the shelter. Not for vanity — for memory. So that when a rescued paw sleeps soundly here for the first time, they know exactly whose kindness made it possible.
                  </p>
                  <Button
                    onClick={scrollToDonate}
                    className="bg-honey hover:bg-honey/85 text-forest rounded-full h-12 px-7 font-semibold mt-2"
                    data-testid="closing-cta"
                  >
                    Add your name <Heart className="w-4 h-4 ml-2 fill-forest" strokeWidth={1.6}/>
                  </Button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ───────── RECENT DONORS ───────── */}
      <section className="py-20 lg:py-28 bg-bone-soft/40" data-testid="recent-donors-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <div className="inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.25em] text-terracotta mb-5">
                <span className="w-10 h-px bg-terracotta" />
                The wall
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl font-light text-forest leading-tight">
                Hands that <em className="italic text-terracotta">built this.</em>
              </h2>
              <p className="text-ink/70 mt-3 max-w-xl">
                Every contribution shows up here within seconds — a living wall of the kindness that's making this shelter real.
              </p>
            </div>
            <div className="text-sm text-ink/60 flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-terracotta" />
              </span>
              Live · updates as donations arrive
            </div>
          </div>

          {recent.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 border border-bone-soft text-center" data-testid="recent-donors-empty">
              <Heart className="w-10 h-10 text-terracotta mx-auto mb-4" strokeWidth={1.3}/>
              <p className="font-serif text-2xl text-forest">Be the first name on this wall.</p>
              <p className="text-ink/60 mt-2">Your donation will show up here for everyone to see (or stay anonymous — your choice).</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recent-donors-grid">
              {recent.map((d, i) => {
                const dt = d.donated_at ? new Date(d.donated_at) : null;
                const when = dt ? dt.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                const isAnon = d.name === "Anonymous donor";
                return (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-5 border border-bone-soft hover:border-terracotta/40 transition-all"
                    data-testid={`recent-donor-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center font-serif text-lg ${isAnon ? "bg-ink/10 text-ink/60" : "bg-terracotta/15 text-terracotta"}`}>
                        {isAnon ? "?" : d.name.trim().charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <p className={`font-medium truncate ${isAnon ? "text-ink/55 italic" : "text-forest"}`}>{d.name}</p>
                          <p className="font-serif text-xl text-terracotta font-medium shrink-0">₹{d.amount_inr.toLocaleString("en-IN")}</p>
                        </div>
                        {d.message && (
                          <p className="text-sm text-ink/70 mt-1.5 italic line-clamp-2">"{d.message}"</p>
                        )}
                        <p className="text-[11px] text-ink/45 uppercase tracking-wider mt-1.5">{when}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-bone via-bone to-bone-soft/40" data-testid="faq-section">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16">

          {/* Header */}
          <div className="text-center mb-14 lg:mb-16 space-y-5">
            <div className="inline-flex items-center gap-2 bg-terracotta/10 text-terracotta px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.25em] font-bold">
              <HelpCircle className="w-3.5 h-3.5" strokeWidth={2}/>
              Questions From Supporters
            </div>
            <h2 className="font-serif text-4xl lg:text-6xl font-light text-forest leading-[1.05] tracking-tight">
              Frequently asked <em className="italic text-terracotta">questions.</em>
            </h2>
            <p className="text-lg lg:text-xl text-ink/75 leading-relaxed max-w-2xl mx-auto">
              We believe every supporter deserves complete transparency about how their kindness helps rescued animals heal, eat, and stay safe.
            </p>
          </div>

          {/* Trust banner */}
          <div className="grid sm:grid-cols-3 gap-3 mb-12" data-testid="faq-trust-cards">
            {[
              { icon: BadgeCheck, label: "100% to rescue, food & shelter", sub: "No middlemen, no overheads" },
              { icon: Receipt, label: "80G tax exempt", sub: "Receipts auto-issued" },
              { icon: Eye, label: "Full transparency", sub: "Monthly utilisation reports" },
            ].map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-bone-soft flex items-center gap-3" data-testid={`faq-trust-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-honey/25 flex items-center justify-center shrink-0">
                  <t.icon className="w-5 h-5 text-forest" strokeWidth={1.6}/>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-forest text-sm leading-tight">{t.label}</p>
                  <p className="text-xs text-ink/55 mt-0.5">{t.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Accordion */}
          <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
            {/* Q1 */}
            <AccordionItem value="q1" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q1">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <UtensilsCrossed className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">How are donations used?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-4 text-ink/80 leading-relaxed">
                  <p>Every rupee is tracked and used directly for the rescued animals. Nothing leaves their plate to pay for things they don't need.</p>
                  <div className="grid sm:grid-cols-2 gap-3 my-4">
                    {[
                      ["42%", "Food, medicines & emergency treatments"],
                      ["28%", "Shelter construction & land development"],
                      ["15%", "Care for disabled/paralyzed rescues"],
                      ["10%", "Staff, transport & rescue operations"],
                      ["5%", "Sterilisation & vaccinations"],
                    ].map(([p, l]) => (
                      <div key={p} className="bg-bone-soft/40 rounded-xl p-4 flex items-baseline gap-3">
                        <span className="font-serif text-2xl text-terracotta font-medium">{p}</span>
                        <span className="text-sm text-ink/75 leading-snug">{l}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-ink/65 italic">We publish a public utilisation summary on the 1st of every month, signed by our auditor.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q6 */}
            <AccordionItem value="q6" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q6">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <Heart className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">Can I sponsor a dog monthly?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Yes — and it's the most meaningful way to support us. Monthly sponsors get a dedicated dog they help care for, with:</p>
                  <ul className="space-y-2 ml-1">
                    <li className="flex items-start gap-2"><PawPrint className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>A welcome photo + story of your sponsored dog</li>
                    <li className="flex items-start gap-2"><PawPrint className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>Monthly photo & health updates</li>
                    <li className="flex items-start gap-2"><PawPrint className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>Free visit days at the sanctuary</li>
                    <li className="flex items-start gap-2"><PawPrint className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>Your name on their kennel plaque</li>
                  </ul>
                  <p className="text-sm text-ink/65 italic">Recurring sponsorship signup launches alongside our new shelter — WhatsApp us to be the first.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q7 */}
            <AccordionItem value="q7" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q7">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <HomeIcon className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">How can I adopt a rescued dog?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>We screen carefully because once a dog leaves us, they should never have to come back. The process takes 7–10 days:</p>
                  <ol className="space-y-2 ml-1 list-decimal list-inside marker:text-terracotta">
                    <li>Browse our <a href="/adopt" className="text-terracotta font-semibold link-underline">Adopt page</a> and pick a dog you connect with.</li>
                    <li>Fill the adoption form — we ask about your home, schedule, family.</li>
                    <li>Two short calls + one in-person meet at the shelter.</li>
                    <li>A trial week at your home — if it doesn't work, no questions asked.</li>
                    <li>Final adoption, lifetime support from our team.</li>
                  </ol>
                  <p>There's no fee. We do request a small refundable deposit (₹2,000) that covers vaccinations.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q8 */}
            <AccordionItem value="q8" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q8">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <Moon className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">Do you rescue injured street dogs at night?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Yes — emergencies don't wait for daylight. Our rapid-response team operates <span className="font-semibold text-forest">24×7</span> across Bhopal. Most of our worst injury cases come in after midnight.</p>
                  <div className="bg-terracotta/8 border border-terracotta/20 rounded-xl p-4 my-3 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-terracotta mt-0.5 shrink-0" strokeWidth={1.6}/>
                    <div>
                      <p className="font-semibold text-forest leading-tight">If you spot an injured animal — call <a href="tel:+917477216753" className="text-terracotta link-underline">+91 74772 16753</a></p>
                      <p className="text-sm text-ink/70 mt-1">Send a location pin on WhatsApp. We typically reach within 60 minutes inside city limits.</p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q9 */}
            <AccordionItem value="q9" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q9">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <Sparkles className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">How do I know my donation is making an impact?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Because we'll show you. Every donor gets:</p>
                  <ul className="space-y-2 ml-1">
                    <li className="flex items-start gap-2"><Eye className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>A monthly impact email — real photos, real names, real updates.</li>
                    <li className="flex items-start gap-2"><Eye className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>A public donor wall that updates the moment you donate.</li>
                    <li className="flex items-start gap-2"><Eye className="w-4 h-4 mt-0.5 text-terracotta shrink-0" strokeWidth={1.8}/>An open invite to visit the shelter and see your money at work.</li>
                  </ul>
                  <p className="font-serif text-lg text-forest italic pt-2">"Transparency isn't a feature for us — it's the entire point."</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q2 */}
            <AccordionItem value="q2" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q2">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <Receipt className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">Will I receive an 80G tax receipt?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Yes — Pawsomeland Foundation is a registered Section 8 non-profit with valid 80G certification. Every donation of ₹100 or more is fully tax-deductible under Section 80G of the Indian Income Tax Act.</p>
                  <p>An instant payment confirmation lands in your inbox right after donating. The official 80G certificate (with your PAN) is emailed at the end of every quarter so you can claim it during tax filing.</p>
                  <div className="mt-4 bg-honey/15 border border-honey/30 rounded-xl px-4 py-3 text-sm text-forest flex items-start gap-2">
                    <BadgeCheck className="w-4 h-4 mt-0.5 shrink-0 text-forest" strokeWidth={2}/>
                    <span>Tip: Add your PAN in the email reply you receive to get the 80G certificate faster.</span>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q3 */}
            <AccordionItem value="q3" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q3">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <MapPin className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">Can I visit the shelter?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Absolutely. We welcome supporters every Saturday and Sunday between <span className="font-semibold text-forest">10 am – 4 pm</span> by prior appointment. Bring your family. Sit with the dogs. See exactly where your money goes.</p>
                  <p>You can also volunteer for a day — feeding, walking, or just hanging out. WhatsApp us on <a href="tel:+917477216753" className="text-terracotta font-semibold link-underline">+91 74772 16753</a> to book a slot.</p>
                  <p className="text-sm text-ink/55">Address: Waterfall Road, Near Gaushala Barkhedi Obdullah, Bhadbhada Ghat, Bhopal.</p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q4 */}
            <AccordionItem value="q4" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q4">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <Stethoscope className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">How do rescued dogs receive treatment?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Every rescue goes through a five-stage care pipeline:</p>
                  <ol className="space-y-2 list-decimal list-inside marker:text-terracotta marker:font-bold marker:font-serif marker:text-lg">
                    <li><span className="text-forest font-semibold">Stabilisation</span> — first 24 hours, on-site vet, fluids, pain relief.</li>
                    <li><span className="text-forest font-semibold">Diagnosis</span> — bloodwork, X-rays, infection panel.</li>
                    <li><span className="text-forest font-semibold">Treatment</span> — surgeries, dressings, behavioural rehab.</li>
                    <li><span className="text-forest font-semibold">Recovery</span> — open enclosures, soft diet, daily check-ups.</li>
                    <li><span className="text-forest font-semibold">Forever home</span> — adoption or permanent sanctuary stay.</li>
                  </ol>
                  <div className="grid grid-cols-3 gap-3 mt-5">
                    {[["3", "Vets on call"], ["24×7", "Emergency line"], ["50+", "Disabled dogs cared for"]].map(([v, l]) => (
                      <div key={l} className="bg-forest text-bone rounded-xl p-3 text-center">
                        <div className="font-serif text-2xl text-honey font-medium">{v}</div>
                        <div className="text-[10px] uppercase tracking-wider text-bone/70 mt-1">{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Q5 */}
            <AccordionItem value="q5" className="bg-white rounded-3xl border border-bone-soft shadow-[0_2px_20px_rgba(0,0,0,0.03)] data-[state=open]:border-terracotta/30 data-[state=open]:shadow-[0_15px_40px_rgba(0,0,0,0.06)] transition-all overflow-hidden" data-testid="faq-q5">
              <AccordionTrigger className="px-6 lg:px-8 py-6 hover:no-underline group">
                <div className="flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-xl bg-terracotta/10 group-data-[state=open]:bg-terracotta flex items-center justify-center shrink-0 transition-colors">
                    <BadgeCheck className="w-5 h-5 text-terracotta group-data-[state=open]:text-bone transition-colors" strokeWidth={1.6}/>
                  </div>
                  <span className="font-serif text-xl lg:text-2xl text-forest font-light">Is this shelter verified?</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-6 lg:px-8 pb-7 pt-1">
                <div className="border-t border-bone-soft pt-5 space-y-3 text-ink/80 leading-relaxed">
                  <p>Yes. Pawsomeland Foundation is registered with the Government of India and holds the following compliances:</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-terracotta" strokeWidth={2}/> Section 8 (Non-profit Company) registered under Companies Act, 2013</li>
                    <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-terracotta" strokeWidth={2}/> 12A & 80G certified for tax exemption</li>
                    <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-terracotta" strokeWidth={2}/> Annual financial statements independently audited</li>
                    <li className="flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-terracotta" strokeWidth={2}/> Razorpay-verified payment gateway (PCI-DSS compliant)</li>
                  </ul>
                  <p className="text-sm text-ink/60">Registration & audit documents are available on request — email us anytime.</p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Closing reassurance */}
          <div className="mt-14 text-center max-w-xl mx-auto">
            <p className="text-ink/70 leading-relaxed">
              Still have questions? Email us at <a href="mailto:pawsomelandfoundation@gmail.com" className="text-terracotta font-semibold link-underline">pawsomelandfoundation@gmail.com</a> — we reply within 24 hours, often sooner.
            </p>
          </div>
        </div>
      </section>

      {/* Success Dialog */}
      <Dialog open={!!success} onOpenChange={(o) => !o && setSuccess(null)}>
        <DialogContent data-testid="donation-success-dialog" className="bg-bone border-bone-soft max-w-md">
          <DialogHeader>
            <CheckCircle2 className="w-14 h-14 text-terracotta mb-2" strokeWidth={1.4}/>
            <DialogTitle className="font-serif text-3xl text-forest font-light">Thank you. Truly.</DialogTitle>
            <DialogDescription className="text-ink/80 text-base leading-relaxed">
              Because of you, <span className="text-forest font-semibold">₹{success?.amount?.toLocaleString("en-IN")}</span>{success?.mock ? " (test mode)" : ""} worth of food, medicine, and shelter is going to a dog who really needed it today. You've made this possible. 🐾
            </DialogDescription>
          </DialogHeader>

          {/* WhatsApp Community CTA */}
          <div className="pt-2 space-y-2.5" data-testid="thank-you-whatsapp-cta">
            <a
              href="https://chat.whatsapp.com/KTPYodo4NTV1xwPCBgRcFt"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2.5 w-full bg-forest hover:bg-forest/90 text-bone rounded-full h-12 px-6 font-semibold text-sm tracking-wide transition-colors"
              data-testid="thank-you-whatsapp-button"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={1.8}/>
              Join our WhatsApp community
            </a>
            <p className="text-xs text-ink/60 leading-relaxed text-center px-2">
              Get behind-the-scenes rescue updates, photos of dogs your donation helps, and be the first to hear about urgent cases and shelter milestones.
            </p>
          </div>

          <DialogFooter>
            <Button onClick={() => setSuccess(null)} variant="ghost" className="text-ink/60 hover:text-forest rounded-full" data-testid="donation-success-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
